export const SWIPE_AXIS_LOCK_PX = 10,
  SWIPE_OPEN_RATIO = 0.5;

export type SwipeState =
  | { phase: 'rest'; open: boolean }
  | { phase: 'pending'; startX: number; startY: number; open: boolean }
  | { phase: 'dragging'; startX: number; baseOffset: number; offset: number }
  | { phase: 'scrolling'; open: boolean }; // gesto vertical: o card não se move até soltar

export type SwipeEvent =
  | { type: 'down'; x: number; y: number }
  | { type: 'move'; x: number; y: number }
  | { type: 'up' }
  | { type: 'cancel' }
  | { type: 'close' };

function clampOffset(offset: number, actionsWidth: number): number {
  return Math.min(0, Math.max(-actionsWidth, offset));
}

/**
 * Trava de eixo no 1º move com max(|dx|,|dy|) >= 10: vertical se |dy| >= |dx|
 * (empate = vertical); offset preso em [-actionsWidth, 0]; up abre se
 * offset <= -actionsWidth * 0.5.
 */
export function swipeReducer(
  s: SwipeState,
  e: SwipeEvent,
  cfg: { actionsWidth: number }
): SwipeState {
  switch (e.type) {
    case 'down':
      return s.phase === 'rest'
        ? { phase: 'pending', startX: e.x, startY: e.y, open: s.open }
        : s;
    case 'move': {
      if (s.phase === 'dragging') {
        return {
          ...s,
          offset: clampOffset(s.baseOffset + e.x - s.startX, cfg.actionsWidth),
        };
      }
      if (s.phase !== 'pending') return s;
      const dx = e.x - s.startX;
      const dy = e.y - s.startY;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_AXIS_LOCK_PX) return s;
      if (Math.abs(dy) >= Math.abs(dx)) {
        return { phase: 'scrolling', open: s.open };
      }
      const baseOffset = s.open ? -cfg.actionsWidth : 0;
      return {
        phase: 'dragging',
        startX: s.startX,
        baseOffset,
        offset: clampOffset(baseOffset + dx, cfg.actionsWidth),
      };
    }
    case 'up':
      if (s.phase === 'dragging') {
        return {
          phase: 'rest',
          open: s.offset <= -cfg.actionsWidth * SWIPE_OPEN_RATIO,
        };
      }
      return s.phase === 'rest' ? s : { phase: 'rest', open: s.open };
    case 'cancel':
      return { phase: 'rest', open: isSwipeOpen(s) };
    case 'close':
      return { phase: 'rest', open: false };
  }
}

export function getOffset(
  s: SwipeState,
  cfg: { actionsWidth: number }
): number {
  if (s.phase === 'dragging') return s.offset;
  return s.open ? -cfg.actionsWidth : 0;
}

/** Open state at rest; while dragging, the state the card had when the drag began. */
export function isSwipeOpen(s: SwipeState): boolean {
  return s.phase === 'dragging' ? s.baseOffset < 0 : s.open;
}
